import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, AlertCircle, CheckCircle, Loader, ArrowLeft, Mic, Square, Radio } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '@/config/firebase';
import { TeamMeetingService } from '@/services/team-meeting.service';
import { AIWorkspaceService } from '@/services/ai-workspace.service';
import { MeetingType } from '@/models/team-meeting.model';
import { PHIAlertBanner } from '@/components/team/PHIAlertBanner';

export const MeetingUpload: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    meetingType: MeetingType.standup as string,
    meetingDate: new Date().toISOString().split('T')[0],
    attendees: '',
    description: '',
    transcriptText: '',
  });

  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [error, setError] = useState('');
  const [phiFlags, setPhiFlags] = useState<any[]>([]);
  const [showPHIWarning, setShowPHIWarning] = useState(false);
  const [validation, setValidation] = useState<any>(null);

  // Live recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [liveTranscript, setLiveTranscript] = useState('');
  const [transcriptionSessionId, setTranscriptionSessionId] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const transcriptionSessionIdRef = useRef<string | null>(null);
  const functions = getFunctions();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleTranscriptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    setFormData(prev => ({ ...prev, transcriptText: text }));

    // Validate and detect PHI as user types
    if (text.length > 50) {
      const validation = AIWorkspaceService.validateTranscript(text);
      setValidation(validation);

      if (validation.isValid && text.length > 100) {
        const detectedPHI = AIWorkspaceService.detectPHI(text);
        setPhiFlags(detectedPHI);
        setShowPHIWarning(detectedPHI.length > 0);
      } else {
        setPhiFlags([]);
        setShowPHIWarning(false);
      }
    } else {
      setValidation(null);
      setPhiFlags([]);
      setShowPHIWarning(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      setFormData(prev => ({ ...prev, transcriptText: text }));

      // Validate and detect PHI
      const validation = AIWorkspaceService.validateTranscript(text);
      setValidation(validation);

      if (validation.isValid) {
        const detectedPHI = AIWorkspaceService.detectPHI(text);
        setPhiFlags(detectedPHI);
        setShowPHIWarning(detectedPHI.length > 0);
      }
    };
    reader.readAsText(file);
  };

  const validateForm = (): boolean => {
    if (!formData.title.trim()) {
      setError('Meeting title is required');
      return false;
    }

    if (!formData.transcriptText.trim()) {
      setError('Meeting transcript is required');
      return false;
    }

    if (validation && !validation.isValid) {
      setError(validation.errors[0] || 'Invalid transcript');
      return false;
    }

    return true;
  };

  // Live recording functions
  const startRecording = async () => {
    try {
      setError('');
      console.log('[MeetingUpload] Starting live recording...');

      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      // Start transcription session
      const startSession = httpsCallable(functions, 'startTranscriptionSession');
      const sessionResult = await startSession({
        meetingTitle: formData.title || 'Untitled Meeting'
      });

      const sessionData = sessionResult.data as { success: boolean; sessionId: string };
      console.log('[MeetingUpload] startTranscriptionSession result:', sessionData);
      if (sessionData.success) {
        setTranscriptionSessionId(sessionData.sessionId);
        transcriptionSessionIdRef.current = sessionData.sessionId;
      } else {
        setError('Failed to start transcription session.');
      }

      // Handle audio data
      mediaRecorder.ondataavailable = async (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);

          // Transcribe this chunk
          const blob = event.data;
          try {
            console.log('[MeetingUpload] Processing audio chunk:', {
              size: blob.size,
              type: blob.type,
            });

            const reader = new FileReader();
            reader.onloadend = async () => {
              try {
                const base64Audio = (reader.result as string).split(',')[1];
                console.log('[MeetingUpload] Base64 audio length:', base64Audio.length);

                // Skip if chunk is too small (less than 1KB)
                if (base64Audio.length < 1000) {
                  console.log('[MeetingUpload] Skipping chunk - too small');
                  return;
                }

                const transcribeAudioFunc = httpsCallable(functions, 'transcribeAudio');
                const result = await transcribeAudioFunc({
                  audioContent: base64Audio,
                  sampleRateHertz: 48000,
                  languageCode: 'en-US',
                  enableSpeakerDiarization: true,
                  diarizationSpeakerCount: 5,
                });

                const transcriptionData = result.data as {
                  success: boolean;
                  transcript: string;
                  speakers?: Array<{ speakerTag: number; text: string }>;
                  error?: string;
                };
                console.log('[MeetingUpload] transcribeAudio result:', {
                  success: transcriptionData.success,
                  transcriptLength: transcriptionData.transcript?.length ?? 0,
                  hasSpeakers: (transcriptionData.speakers?.length ?? 0) > 0,
                  error: transcriptionData.error,
                });

                if (!transcriptionData.success && transcriptionData.error) {
                  console.error('[MeetingUpload] Transcription error:', transcriptionData.error);
                  setError(`Transcription error: ${transcriptionData.error}`);
                }

                if (transcriptionData.success && transcriptionData.transcript) {
                // Format transcript with speakers if available
                let formattedText = transcriptionData.transcript;
                if (transcriptionData.speakers && transcriptionData.speakers.length > 0) {
                  formattedText = transcriptionData.speakers
                    .map(s => `Speaker ${s.speakerTag}: ${s.text}`)
                    .join('\n');
                }

                // Append to live transcript
                setLiveTranscript(prev => {
                  const updated = prev ? prev + '\n\n' + formattedText : formattedText;
                  setFormData(current => ({ ...current, transcriptText: updated }));
                  return updated;
                });

                // Update session
                if (transcriptionSessionIdRef.current) {
                  const updateSession = httpsCallable(functions, 'updateTranscriptionSession');
                  await updateSession({
                    sessionId: transcriptionSessionIdRef.current,
                    transcript: formattedText,
                    append: true,
                  });
                  console.log('[MeetingUpload] updateTranscriptionSession appended chunk.');
                }
              }
              } catch (err: any) {
                console.error('[MeetingUpload] Error in transcription callback:', err);
              }
            };
            reader.onerror = (error) => {
              console.error('[MeetingUpload] FileReader error:', error);
            };
            reader.readAsDataURL(blob);
          } catch (err) {
            console.error('[MeetingUpload] Error transcribing audio chunk:', err);
          }
        }
      };

      mediaRecorder.onstop = () => {
        stream.getTracks().forEach(track => track.stop());
      };

      // Start recording with chunks every 5 seconds
      mediaRecorder.start(5000);
      setIsRecording(true);
      setRecordingTime(0);
      console.log('[MeetingUpload] Recording started.');

      // Start timer
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (err: any) {
      console.error('[MeetingUpload] Error starting recording:', err);
      setError(err.message || 'Failed to start recording. Please check microphone permissions.');
    }
  };

  const stopRecording = async () => {
    if (mediaRecorderRef.current && isRecording) {
      console.log('[MeetingUpload] Stopping recording...');
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      // Stop timer
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }

      // End transcription session
      const sessionId = transcriptionSessionIdRef.current;
      if (sessionId) {
        try {
          const endSession = httpsCallable(functions, 'endTranscriptionSession');
          const result = await endSession({ sessionId });
          const data = result.data as { success: boolean; transcript: string };
          console.log('[MeetingUpload] endTranscriptionSession result:', {
            success: data.success,
            transcriptLength: data.transcript?.length ?? 0,
          });

          if (data.success) {
            const finalTranscript = data.transcript || liveTranscript || formData.transcriptText;
            if (finalTranscript) {
              setFormData(prev => ({ ...prev, transcriptText: finalTranscript }));
              setLiveTranscript(finalTranscript);
            }
          }
        } catch (err) {
          console.error('[MeetingUpload] Error ending transcription session:', err);
        }
      }
    }
  };

  const formatRecordingTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Debug: Log formData to see what we have
    console.log('[MeetingUpload] Form data on submit:', formData);
    console.log('[MeetingUpload] meetingType value:', formData.meetingType);
    console.log('[MeetingUpload] meetingType type:', typeof formData.meetingType);

    setIsUploading(true);
    setError('');

    try {
      // Get current user
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('You must be logged in to create a meeting');
      }

      // 1. Create meeting document
      setProcessingStatus('Creating meeting...');

      // Ensure meetingType is valid
      const meetingType = formData.meetingType || MeetingType.standup;
      console.log('[MeetingUpload] Using meetingType:', meetingType);

      const meetingId = await TeamMeetingService.createMeeting(
        {
          title: formData.title,
          meetingType: meetingType as MeetingType,
          meetingDate: new Date(formData.meetingDate),
          attendees: formData.attendees.split(',').map(a => a.trim()).filter(Boolean),
          description: formData.description,
          transcriptText: formData.transcriptText,
          accessRoles: ['operationsAdmin', 'executive'],
          isPublic: false,
        },
        currentUser.uid,
        currentUser.displayName || currentUser.email || 'Unknown User'
      );

      console.log('[MeetingUpload] Meeting created with ID:', meetingId);
      console.log('[MeetingUpload] Transcript text length:', formData.transcriptText.length);

      // 2. Process transcript with AI (only if we have transcript text)
      if (!formData.transcriptText || formData.transcriptText.trim().length === 0) {
        console.log('[MeetingUpload] No transcript text, skipping AI processing');
        // Meeting created but no transcript to process
        setIsUploading(false);
        setTimeout(() => {
          navigate(`/team-workspace/meetings/${meetingId}`);
        }, 500);
        return;
      }

      setIsUploading(false);
      setIsProcessing(true);
      setProcessingStatus('Processing transcript with AI...');

      const estimatedTime = AIWorkspaceService.estimateProcessingTime(formData.transcriptText);
      setProcessingStatus(`Processing... (estimated ${estimatedTime} seconds)`);

      console.log('[MeetingUpload] Calling processMeetingTranscript with:', {
        meetingId,
        transcriptLength: formData.transcriptText.length
      });

      const result = await TeamMeetingService.processMeetingTranscript(meetingId, formData.transcriptText);

      if (result.success) {
        setProcessingStatus('Processing complete!');
        setTimeout(() => {
          navigate(`/team-workspace/meetings/${meetingId}`);
        }, 1000);
      } else {
        throw new Error(result.error || 'Processing failed');
      }
    } catch (err: any) {
      console.error('Error uploading meeting:', err);
      setError(err.message || 'Failed to upload meeting. Please try again.');
      setIsUploading(false);
      setIsProcessing(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/team-workspace')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Workspace
        </button>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <Upload className="w-8 h-8 mr-3 text-blue-600" />
          Upload Meeting Transcript
        </h1>
        <p className="text-gray-600 mt-1">
          Upload a meeting transcript for AI-powered analysis and extraction
        </p>
      </div>

      {/* PHI Warning */}
      {showPHIWarning && phiFlags.length > 0 && (
        <PHIAlertBanner
          phiFlags={phiFlags}
          onDismiss={() => setShowPHIWarning(false)}
          className="mb-6"
        />
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow border border-gray-200 p-6">
        {/* Meeting Title */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Meeting Title *
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="e.g., Q1 Planning Meeting"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={isUploading || isProcessing}
            required
          />
        </div>

        {/* Meeting Type */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Meeting Type
          </label>
          <select
            name="meetingType"
            value={formData.meetingType}
            onChange={handleInputChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={isUploading || isProcessing}
          >
            <option value={MeetingType.standup}>Stand-up</option>
            <option value={MeetingType.planning}>Planning</option>
            <option value={MeetingType.retrospective}>Retrospective</option>
            <option value={MeetingType.oneOnOne}>One-on-One</option>
            <option value={MeetingType.allHands}>All Hands</option>
            <option value={MeetingType.training}>Training</option>
            <option value={MeetingType.client}>Client Meeting</option>
            <option value={MeetingType.other}>Other</option>
          </select>
        </div>

        {/* Meeting Date */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Meeting Date
          </label>
          <input
            type="date"
            name="meetingDate"
            value={formData.meetingDate}
            onChange={handleInputChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={isUploading || isProcessing}
          />
        </div>

        {/* Attendees */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Attendees (comma-separated)
          </label>
          <input
            type="text"
            name="attendees"
            value={formData.attendees}
            onChange={handleInputChange}
            placeholder="e.g., John, Sarah, Mike"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={isUploading || isProcessing}
          />
        </div>

        {/* Description */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description (optional)
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Brief description or notes about the meeting"
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={isUploading || isProcessing}
          />
        </div>

        {/* Transcript */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Meeting Transcript *
          </label>

          {/* Live Recording Button */}
          <div className="mb-4">
            <button
              type="button"
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isUploading || isProcessing}
              className={`w-full px-4 py-6 border-2 rounded-lg font-medium transition-all ${
                isRecording
                  ? 'border-red-500 bg-red-50 hover:bg-red-100 text-red-700'
                  : 'border-green-500 bg-green-50 hover:bg-green-100 text-green-700'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <div className="flex items-center justify-center">
                {isRecording ? (
                  <>
                    <Square className="w-6 h-6 mr-3" />
                    <div>
                      <div className="flex items-center">
                        <Radio className="w-4 h-4 mr-2 animate-pulse text-red-600" />
                        <span>Stop Recording - {formatRecordingTime(recordingTime)}</span>
                      </div>
                      <div className="text-sm font-normal mt-1">Recording in progress...</div>
                    </div>
                  </>
                ) : (
                  <>
                    <Mic className="w-6 h-6 mr-3" />
                    <div>
                      <div>Start Live Recording</div>
                      <div className="text-sm font-normal mt-1">
                        Record in-person meeting with real-time transcription
                      </div>
                    </div>
                  </>
                )}
              </div>
            </button>
          </div>

          <div className="text-center text-gray-500 text-sm mb-4">or</div>

          {/* File Upload Option */}
          <div className="mb-4">
            <label className="flex items-center justify-center w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 cursor-pointer transition-colors">
              <div className="text-center">
                <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <span className="text-sm text-gray-600">
                  Click to upload transcript file (.txt)
                </span>
              </div>
              <input
                type="file"
                accept=".txt"
                onChange={handleFileUpload}
                className="hidden"
                disabled={isUploading || isProcessing || isRecording}
              />
            </label>
          </div>

          <div className="text-center text-gray-500 text-sm mb-4">or paste text below</div>

          {/* Text Area */}
          <textarea
            name="transcriptText"
            value={formData.transcriptText}
            onChange={handleTranscriptChange}
            placeholder={isRecording ? "Live transcript will appear here..." : "Paste meeting transcript here... (minimum 50 characters)"}
            rows={12}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
            disabled={isUploading || isProcessing || isRecording}
            required
          />

          {/* Character Count */}
          <div className="flex items-center justify-between mt-2 text-sm">
            <div className="text-gray-500">
              {formData.transcriptText.length.toLocaleString()} characters
              {formData.transcriptText.length > 0 && (
                <span className="ml-2">
                  (~{AIWorkspaceService.estimateProcessingTime(formData.transcriptText)}s processing time)
                </span>
              )}
            </div>
            {validation && (
              <div>
                {validation.isValid ? (
                  <span className="text-green-600 flex items-center">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Valid
                  </span>
                ) : (
                  <span className="text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {validation.errors[0]}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Validation Warnings */}
          {validation && validation.warnings.length > 0 && (
            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm font-medium text-yellow-800 mb-1">Warnings:</p>
              <ul className="text-sm text-yellow-700 list-disc list-inside">
                {validation.warnings.map((warning: string, i: number) => (
                  <li key={i}>{warning}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-red-800">{error}</div>
          </div>
        )}

        {/* Processing Status */}
        {(isUploading || isProcessing) && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center">
              <Loader className="w-5 h-5 text-blue-600 mr-3 animate-spin" />
              <div className="flex-1">
                <div className="text-sm font-medium text-blue-900">{processingStatus}</div>
                {isProcessing && (
                  <div className="text-xs text-blue-700 mt-1">
                    The AI is analyzing your transcript. This may take a moment...
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex items-center justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/team-workspace')}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            disabled={isUploading || isProcessing}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
            disabled={isUploading || isProcessing || !formData.title || !formData.transcriptText}
          >
            {isUploading || isProcessing ? (
              <>
                <Loader className="w-5 h-5 mr-2 animate-spin" />
                {isUploading ? 'Uploading...' : 'Processing...'}
              </>
            ) : (
              <>
                <Upload className="w-5 h-5 mr-2" />
                Upload & Process
              </>
            )}
          </button>
        </div>
      </form>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <h3 className="text-sm font-medium text-gray-900 mb-2">How it works:</h3>
        <div className="space-y-3">
          <div>
            <h4 className="text-sm font-medium text-gray-800 mb-1">📹 Live Recording</h4>
            <p className="text-sm text-gray-700">
              Click "Start Live Recording" to capture in-person meetings. Real-time transcription
              with speaker identification happens automatically using Google Cloud Speech-to-Text.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-800 mb-1">🤖 AI Analysis</h4>
            <p className="text-sm text-gray-700">
              After recording or uploading, AI (Gemini 2.0 Flash) automatically extracts decisions,
              tasks, risks, and questions from your transcript.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-800 mb-1">🔒 PHI Detection</h4>
            <p className="text-sm text-gray-700">
              Protected Health Information is automatically detected and flagged for HIPAA compliance.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingUpload;
